// layouts/catalogos/parametros/index.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import { listParametros, deleteParametro } from "../services/parametros";
import buildTable, { columns as paramCols } from "./datosparametros";

import api from "services/api";

const toArray = (v) => (Array.isArray(v) ? v : []);

// error helper
const errMsg = (e) =>
  e?.response?.data?.message ||
  (Array.isArray(e?.response?.data)
    ? e.response.data.map((x) => x?.message || x).join(", ")
    : null) ||
  e?.message ||
  "Error";

// Mapa entre nombre de columna (UI) y campo real en BD
const SORT_FIELD_MAP = {
  formato: "format_id",
  vigencia: "since",
  oro: "gold",
  platino: "platinum",
  diamante: "diamond",
};

export default function Parametros() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // paginación
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // mapa: id formato -> label
  const [formatsMap, setFormatsMap] = useState({});

  // diálogo y snackbar
  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openSnack = (m, s = "success") => setSnack({ open: true, message: m, severity: s });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // estado de ordenamiento
  const [sort, setSort] = useState({
    uiField: "formato",
    apiField: "format_id",
    direction: "asc", // "asc" | "desc"
  });

  // fetch server-side de parámetros
  const fetchList = async ({
    pageIndex = page,
    pageSize = rowsPerPage,
    sortField = sort.apiField,
    sortDirection = sort.direction,
  } = {}) => {
    setLoading(true);
    setErr("");

    try {
      const limit = pageSize;
      const offset = pageIndex * pageSize;

      const sortParam =
        sortField != null
          ? { [sortField]: sortDirection === "asc" ? 1 : -1 }
          : null;

      const res = await listParametros({
        __limit: limit,
        __offset: offset,
        ...(sortParam ? { __sort: sortParam } : {}),
      });

      const rows = Array.isArray(res)
        ? res
        : res.rows || [];

      const totalCount = !Array.isArray(res)
        ? Number(res.total || rows.length)
        : rows.length;

      setItems(rows);
      setTotal(totalCount);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  // cargar formatos para mapear format_id -> nombre
  const loadFormats = async () => {
    try {
      const { data } = await api.get("/format");
      const list =
        Array.isArray(data) ? data :
        Array.isArray(data?.data) ? data.data :
        Array.isArray(data?.page) ? data.page :
        Array.isArray(data?.results) ? data.results : [];
      const map = {};
      list.forEach((f) => {
        const id = String(f.id ?? f._id);
        const name = f.name ?? f.nombre ?? `Formato ${id}`;
        const code = f.code ?? f.codigo;
        map[id] = code ? `${name} (${code})` : name;
      });
      setFormatsMap(map);
    } catch (e) {
      console.warn("[PARAM/INDEX] formatos:", e);
    }
  };

  // carga inicial
  useEffect(() => {
    fetchList({
      pageIndex: 0,
      pageSize: rowsPerPage,
      sortField: sort.apiField,
      sortDirection: sort.direction,
    });
    loadFormats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // click en encabezado de columna
  const handleSortChange = (columnName) => {
    const apiField = SORT_FIELD_MAP[columnName] || columnName;

    setPage(0);

    setSort((prev) => {
      let direction = "asc";

      if (prev.uiField === columnName && prev.direction === "asc") {
        direction = "desc";
      }

      const next = {
        uiField: columnName,
        apiField,
        direction,
      };

      fetchList({
        pageIndex: 0,
        pageSize: rowsPerPage,
        sortField: next.apiField,
        sortDirection: next.direction,
      });

      return next;
    });
  };

  // construir columnas/filas
  const { columns, rows } = useMemo(
    () =>
      buildTable(toArray(items), (it) => {
        const id = it.id ?? it._id;

        const formatName =
          formatsMap[String(it.format_id)] ??
          (it.format?.name ??
            it.format?.nombre ??
            (it.format_id != null ? `Formato #${it.format_id}` : "—"));

        return {
          id,
          format: formatName,
          since: it.since,
          until: it.until,
          gold: it.gold,
          platinum: it.platinum,
          diamond: it.diamond,
          createdAt: it.created_at ?? it.createdAt,
          onEdit: () => navigate(`/catalogos/parametros/${id}/editar`),
          onDelete: () =>
            setPending({
              id,
              label: `${formatName} • ${it.since ?? ""} - ${it.until ?? ""}`,
            }),
        };
      }),
    [items, formatsMap, navigate]
  );

  // helpers para búsqueda sobre la página actual
  const getText = (cell) =>
    typeof cell === "string"
      ? cell
      : cell?.props?.children?.props?.children ?? cell?.props?.children ?? "";

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) => {
      const texto = [
        getText(r.formato),
        getText(r.vigencia),
        getText(r.oro),
        getText(r.platino),
        getText(r.diamante),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(s);
    });
  }, [rows, search]);

  // eliminar
  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await deleteParametro(pending.id);
      openSnack(`Eliminado: ${pending.label}`, "success");

      await fetchList({
        pageIndex: page,
        pageSize: rowsPerPage,
        sortField: sort.apiField,
        sortDirection: sort.direction,
      });
    } catch (e) {
      const m = errMsg(e);
      setErr(m);
      openSnack(`No se pudo eliminar: ${m}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Parámetros"
        search={search}
        onSearchChange={setSearch}
        addText="Agregar"
        onAdd={() => navigate("/catalogos/parametros/nuevo")}
        columns={paramCols}
        rows={filteredRows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        sortField={sort.uiField}
        sortDirection={sort.direction}
        onSortChange={handleSortChange}
        paginationProps={
          !loading && {
            count: total,
            page,
            rowsPerPage,
            onPageChange: (_e, newPage) => {
              setPage(newPage);
              fetchList({
                pageIndex: newPage,
                pageSize: rowsPerPage,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            onRowsPerPageChange: (e) => {
              const newSize = parseInt(e.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
              fetchList({
                pageIndex: 0,
                pageSize: newSize,
                sortField: sort.apiField,
                sortDirection: sort.direction,
              });
            },
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar parámetro"
        description={pending ? `¿Seguro que deseas eliminar "${pending.label}"?` : ""}
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}
